import React, { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Check, ChevronDown, Cpu, Zap, Shield } from "lucide-react";

export default function ModelSelector({ value, onChange, compact = false }) {
  const [models, setModels] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    http.get("/models").then((r) => setModels(r.data.models));
  }, []);

  const selected = models.find((m) => m.id === value) || models[0];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="model-selector-trigger"
          className={`flex items-center gap-2 rounded-lg border border-slate-200 bg-white ${compact ? "px-2.5 py-1.5 text-xs" : "px-3 py-2 text-sm"} hover:border-emerald-400 transition-colors`}
        >
          <Cpu className={`${compact ? "h-3.5 w-3.5" : "h-4 w-4"} text-emerald-700`} />
          <span className="font-medium text-slate-800">
            {selected ? selected.name : "Select model"}
          </span>
          {selected && !compact && (
            <span className="text-xs font-mono text-slate-400">· {selected.vendor}</span>
          )}
          <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-1" align="start" data-testid="model-selector-panel">
        {models.map((m) => (
          <button
            key={m.id}
            data-testid={`model-option-${m.id}`}
            onClick={() => { onChange(m.id); setOpen(false); }}
            className={`w-full text-left rounded-md p-3 flex flex-col gap-1 transition-colors ${
              m.id === value ? "bg-emerald-50" : "hover:bg-slate-50"
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-sm text-slate-900">{m.name}</span>
                {m.id === value && <Check className="h-3.5 w-3.5 text-emerald-700" />}
              </div>
              <span className="text-[10px] font-mono uppercase text-slate-500">{m.vendor}</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-600">
              <span className="inline-flex items-center gap-1"><Zap className="h-3 w-3" />{m.speed}</span>
              <span className="text-slate-300">·</span>
              <span>{m.context} ctx</span>
              {m.hipaa_tuned && <>
                <span className="text-slate-300">·</span>
                <span className="inline-flex items-center gap-1 text-sky-700"><Shield className="h-3 w-3" />HIPAA-aware</span>
              </>}
            </div>
            <span className="text-[11px] text-emerald-800/80 font-medium">{m.badge}</span>
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}
